import { useState, useEffect } from "react";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import Switch from "@mui/material/Switch";
import { db } from "../../../Firebase";
import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

export default function useAchieversData() {
  const [achievers, setAchievers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAchievers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "achievers"));
        const achieversData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAchievers(achieversData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching achievers: ", error);
        setLoading(false);
      }
    };

    fetchAchievers();
  }, []);

  const toggleFrameCollected = async (achieverId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const achieverRef = doc(db, "achievers", achieverId);
      await updateDoc(achieverRef, {
        isFrameCollected: newStatus,
      });
      setAchievers((prevAchievers) =>
        prevAchievers.map((achiever) =>
          achiever.id === achieverId ? { ...achiever, isFrameCollected: newStatus } : achiever
        )
      );
    } catch (error) {
      console.error("Error updating frame collected status: ", error);
    }
  };

  const formatDate = (timestamp) => {
    return timestamp ? new Date(timestamp).toLocaleDateString() : "N/A";
  };

  const Author = ({ name }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {name}
        </MDTypography>
      </MDBox>
    </MDBox>
  );

  const ImageRenderer = ({ imageUrl }) =>
    imageUrl ? (
      <img
        src={imageUrl}
        alt="Selected Frame"
        style={{ width: "50px", height: "auto", objectFit: "cover" }}
      />
    ) : (
      <MDTypography variant="caption">No Image</MDTypography>
    );

  Author.propTypes = {
    name: PropTypes.string.isRequired,
    image: PropTypes.string,
  };

  ImageRenderer.propTypes = {
    imageUrl: PropTypes.string,
  };

  const columns = [
    { Header: "achiever", accessor: "achiever", width: "20%", align: "left" },
    { Header: "mobile", accessor: "mobileNumber", align: "left" },
    { Header: "city", accessor: "city", align: "left" },
    { Header: "address", accessor: "address", align: "left" },
    { Header: "pin", accessor: "pinCode", align: "left" },
    { Header: "request date", accessor: "requestSubmitDate", align: "center" },
    { Header: "mala count", accessor: "malaCountWhenFrameRequested", align: "center" },
    { Header: "selected frame", accessor: "selectedGodFrameImage", align: "center" },
    { Header: "dial-country", accessor: "dialCode", align: "center" },
    { Header: "frame collected", accessor: "isFrameCollected", align: "center" },
  ];

  const rows = achievers.map((achiever) => ({
    achiever: <Author name={achiever.fullName} />,
    mobileNumber: <MDTypography variant="caption">{achiever.mobileNumber}</MDTypography>,
    city: <MDTypography variant="caption">{achiever.city}</MDTypography>,
    address: <MDTypography variant="caption">{achiever.address}</MDTypography>,
    pinCode: <MDTypography variant="caption">{achiever.pinCode}</MDTypography>,
    requestSubmitDate: (
      <MDTypography variant="caption">{formatDate(achiever.requestSubmitDate)}</MDTypography>
    ),
    malaCountWhenFrameRequested: (
      <MDTypography variant="caption" fontWeight="medium">
        {achiever.malaCountWhenFrameRequested || "-"}
      </MDTypography>
    ),
    selectedGodFrameImage: <ImageRenderer imageUrl={achiever.selectedGodFrameImage} />,
    dialCode: (
      <MDTypography variant="caption">
        {achiever.dialCode} - {achiever.countryCode}
      </MDTypography>
    ),
    isFrameCollected: (
      <MDBox display="flex" flexDirection="column" alignItems="center">
        <Switch
          checked={achiever.isFrameCollected || false}
          onChange={() => toggleFrameCollected(achiever.id, achiever.isFrameCollected)}
          color="primary"
        />
        <MDTypography variant="caption">
          {achiever.isFrameCollected ? "Collected" : "Not Collected"}
        </MDTypography>
      </MDBox>
    ),
  }));

  return {
    columns,
    rows,
    loading,
    totalAchievers: achievers.length,
    achievers,
  };
}
